import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Manages the active PJ account context.
 *
 * When the user selects a PJ account to operate on, this service stores
 * the banking_account.id (PK) so that the HTTP interceptor can inject
 * the `x-pj-account-id` header into all banking API requests.
 *
 * When null, the user is operating on their personal (PF) account
 * and no header is sent.
 */
@Injectable({
    providedIn: 'root'
})
export class PjAccountContextService {

    private activePjAccountId = new BehaviorSubject<string | null>(null);

    /**
     * Sets the active PJ account. Pass null to switch back to personal account.
     */
    public setActivePjAccount(bankingAccountId: string | null): void {
        this.activePjAccountId.next(bankingAccountId);
        if (bankingAccountId) {
            sessionStorage.setItem('pjAccountId', bankingAccountId);
        } else {
            sessionStorage.removeItem('pjAccountId');
        }
    }

    /**
     * Returns the active PJ account ID, or null if operating on personal account.
     */
    public getActivePjAccountId(): string | null {
        return this.activePjAccountId.getValue()
            || sessionStorage.getItem('pjAccountId')
            || null;
    }

    /**
     * Observable for components that need to react to PJ context changes.
     */
    public getActivePjAccount$(): Observable<string | null> {
        return this.activePjAccountId.asObservable();
    }

    /**
     * Whether the user is currently operating on a PJ account.
     */
    public isOperatingAsPj(): boolean {
        return !!this.getActivePjAccountId();
    }

    /**
     * Clears the PJ context (back to personal account).
     */
    public clearPjContext(): void {
        this.setActivePjAccount(null);
    }
}
